import type { MentionNodeAttrs } from "@tiptap/extension-mention";
import { ReactRenderer, type Editor } from "@tiptap/react";
import type { SuggestionOptions, SuggestionProps } from "@tiptap/suggestion";

import {
  filterColleagues,
  MentionList,
  type Colleague,
  type MentionListHandle,
  type MentionListProps,
  type MentionListState,
  type PickerAgent,
} from "./MentionList";

/** The server refuses more than 20 mentions in one note; the picker stops there (T-02-30). */
export const MAX_MENTIONS = 20;

/** The picker never lists more than this many rows: the panel scrolls, the DOM stays small. */
const MAX_ROWS = 50;

/**
 * What the picker knows about the tenant's colleagues. The suggestion plugin runs outside
 * React's render and reads the latest answer from here at the moment of every keystroke;
 * the composer feeds it from an effect through `update`.
 */
export class PickerContext {
  agents: readonly PickerAgent[] = [];
  /** Where the `agents` query stands: still loading, failed, or answered. */
  status: "loading" | "error" | "ready" = "loading";
  viewerId: string | undefined;
  /**
   * Set by the open picker, called when `agents` or `status` change under it (the query
   * answers while the picker is already open).
   */
  refresh: (() => void) | null = null;

  constructor(viewerId: string | undefined) {
    this.viewerId = viewerId;
  }

  update(
    agents: readonly PickerAgent[],
    status: PickerContext["status"],
    viewerId: string | undefined,
  ): void {
    this.agents = agents;
    this.status = status;
    this.viewerId = viewerId;
    this.refresh?.();
  }
}

type PickerProps = SuggestionProps<Colleague, MentionNodeAttrs>;

export const countMentions = (editor: Editor): number => {
  let count = 0;
  editor.state.doc.descendants((node) => {
    if (node.type.name === "mention") count += 1;
  });
  return count;
};

function pickerView(
  editor: Editor,
  query: string,
  context: PickerContext,
): { items: Colleague[]; state: MentionListState } {
  if (context.status !== "ready") return { items: [], state: context.status };
  if (countMentions(editor) >= MAX_MENTIONS) return { items: [], state: "cap" };
  return {
    items: filterColleagues(context.agents, query, context.viewerId).slice(
      0,
      MAX_ROWS,
    ),
    state: "ready",
  };
}

// Only ever incremented synchronously inside the editor's event handlers (one browser tab,
// one thread): it makes the listbox ids unique when two composers are open at once.
let pickerCounter = 0;

/**
 * The `suggestion` option of the Mention extension: `@` at the start of a line or after
 * whitespace opens the picker (D-05: never inside a word such as an email address), the
 * panel is rendered by `MentionList` and positioned under the caret by Tiptap's own
 * Floating UI mount (flips above when there is no room below).
 */
export function createMentionSuggestion(
  context: PickerContext,
): Omit<SuggestionOptions<Colleague, MentionNodeAttrs>, "editor"> {
  return {
    char: "@",
    allowedPrefixes: [" ", "\n"],
    // The default command inserts the atomic node plus a trailing space.
    items: ({ query, editor }) =>
      pickerView(editor, query, context).items,
    floatingUi: { strategy: "fixed" },
    render: () => {
      const listId = `note-mention-list-${(pickerCounter += 1)}`;
      let renderer: ReactRenderer<MentionListHandle, MentionListProps> | null =
        null;
      let unmount: (() => void) | null = null;
      let latest: PickerProps | null = null;

      const setEditorAria = (editor: Editor, activeId: string | null) => {
        const dom = editor.view.dom;
        dom.setAttribute("aria-haspopup", "listbox");
        dom.setAttribute("aria-expanded", "true");
        dom.setAttribute("aria-controls", listId);
        if (activeId) dom.setAttribute("aria-activedescendant", activeId);
        else dom.removeAttribute("aria-activedescendant");
      };
      const clearEditorAria = (editor: Editor) => {
        const dom = editor.view.dom;
        dom.removeAttribute("aria-haspopup");
        dom.removeAttribute("aria-expanded");
        dom.removeAttribute("aria-controls");
        dom.removeAttribute("aria-activedescendant");
      };

      // Stable for the life of this picker, so the list's effect does not run on every update.
      const onActiveChange = (optionId: string | null) => {
        if (latest) setEditorAria(latest.editor, optionId);
      };

      const propsFor = (props: PickerProps, items: Colleague[], state: MentionListState) => ({
        id: listId,
        items,
        state,
        onSelect: (colleague: Colleague) =>
          props.command({ id: colleague.id, label: colleague.label }),
        onActiveChange,
      });

      return {
        onStart: (props) => {
          latest = props;
          const view = pickerView(props.editor, props.query, context);
          const created = new ReactRenderer<MentionListHandle, MentionListProps>(
            MentionList,
            {
              editor: props.editor,
              props: propsFor(props, view.items, view.state),
            },
          );
          renderer = created;
          // Above dialogs and the thread; Tiptap appends it to the body and keeps it under the caret.
          created.element.style.zIndex = "50";
          unmount = props.mount(created.element);
          setEditorAria(props.editor, null);
          context.refresh = () => {
            if (!latest || !renderer) return;
            const fresh = pickerView(latest.editor, latest.query, context);
            renderer.updateProps(propsFor(latest, fresh.items, fresh.state));
          };
        },
        onUpdate: (props) => {
          latest = props;
          const view = pickerView(props.editor, props.query, context);
          renderer?.updateProps(propsFor(props, view.items, view.state));
        },
        onKeyDown: ({ event }) => renderer?.ref?.onKeyDown({ event }) ?? false,
        onExit: (props) => {
          context.refresh = null;
          unmount?.();
          unmount = null;
          renderer?.destroy();
          renderer = null;
          latest = null;
          if (!props.editor.isDestroyed) clearEditorAria(props.editor);
        },
      };
    },
  };
}
